class VariantReactive extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Record<string, any>} */
  variantMap = {};
  /** @type {any} */
  fallback = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const mapEl = this.querySelector(
      'script[type="application/json"][data-variant-map]',
    );
    try {
      this.variantMap = JSON.parse(mapEl?.textContent ?? "{}");
    } catch {
      return;
    }

    const fallbackEl = this.querySelector(
      'script[type="application/json"][data-variant-fallback]',
    );
    if (fallbackEl) {
      try {
        this.fallback = JSON.parse(fallbackEl.textContent);
      } catch {
        this.fallback = null;
      }
    }

    this.#update(this.#getInitialVariantId());

    document.addEventListener(
      "variant:update",
      (event) => {
        const customEvent = /** @type {CustomEvent} */ (event);
        const id = customEvent.detail?.resource?.id;
        if (id) this.#update(String(id));
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {string | null} variantId
   */
  #update(variantId) {
    const data =
      (variantId && this.variantMap[variantId]) ?? this.fallback ?? null;
    this.render(data, variantId);
  }

  /**
   * Override.
   * @param {any} _data
   * @param {string | null} _variantId
   */
  render(_data, _variantId) {}

  #getInitialVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get("variant");
    if (fromUrl) return fromUrl;
    if (this.dataset.currentVariant) return this.dataset.currentVariant;
    const formInput = /** @type {HTMLInputElement | null} */ (
      document.querySelector('form[action*="/cart/add"] input[name="id"]')
    );
    return formInput?.value || null;
  }
}

/* ------------------------------------------------------------------ */
/* <variant-warranty>                                                  */
/*   Map value: string (warranty text). Hides itself when empty.       */
/*   Children: [data-body] target paragraph + the JSON map.            */
/* ------------------------------------------------------------------ */
class VariantWarranty extends VariantReactive {
  /**
   * @param {string | null} text
   */
  render(text) {
    const body = this.querySelector("[data-body]");
    if (!body) return;

    if (!text) {
      this.classList.add("is-hidden");
      body.replaceChildren();
      return;
    }

    this.classList.remove("is-hidden");
    body.replaceChildren();
    String(text)
      .split("\n")
      .forEach((line, i) => {
        if (i > 0) body.append(document.createElement("br"));
        body.append(document.createTextNode(line));
      });
  }
}
customElements.define("variant-warranty", VariantWarranty);

/* ------------------------------------------------------------------ */
/* <variant-downloads>                                                 */
/*   Map value: [{ l: label, u: url }, ...].                           */
/*   Children: [data-list] target + JSON map + [data-icon-template].   */
/* ------------------------------------------------------------------ */
class VariantDownloads extends VariantReactive {
  /**
   * @param {Array<{ l: string; u: string }> | null} items
   */
  render(items) {
    const list = this.querySelector("[data-list]");
    if (!list) return;

    if (!items || items.length === 0) {
      this.classList.add("is-hidden");
      list.replaceChildren();
      return;
    }

    const iconTpl = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector("template[data-icon-template]")
    );

    this.classList.remove("is-hidden");
    list.replaceChildren(
      ...items.map(({ l, u }) => {
        const a = document.createElement("a");
        a.className = "download-link";
        a.href = u;
        a.target = "_blank";
        a.rel = "noopener";

        const label = document.createElement("span");
        label.className = "download-label";
        label.textContent = l ?? "";

        const icon = document.createElement("span");
        icon.className = "download-icon";
        if (iconTpl) icon.append(iconTpl.content.cloneNode(true));

        a.append(label, icon);
        return a;
      }),
    );
  }
}
customElements.define("variant-downloads", VariantDownloads);

/* ------------------------------------------------------------------ */
/* <variant-box-list>                                                  */
/*   Map value: [{ q: qty, n: name }, ...]. Falls back to <fallback>.  */
/*   Children: [data-list] target + JSON map + optional fallback.      */
/*   Emits `variant-box-list:empty` when no items are available so the */
/*   surrounding tab/section can collapse to a sibling tab.            */
/* ------------------------------------------------------------------ */
class VariantBoxList extends VariantReactive {
  /**
   * @param {Array<{ q: string | number; n: string }> | null} items
   */
  render(items) {
    const list = this.querySelector("[data-list]");
    if (!list) return;

    if (!items || items.length === 0) {
      this.classList.add("is-hidden");
      list.replaceChildren();
      this.dispatchEvent(
        new CustomEvent("variant-box-list:empty", { bubbles: true }),
      );
      return;
    }

    this.classList.remove("is-hidden");
    list.replaceChildren(
      ...items.map(({ q, n }) => {
        const li = document.createElement("li");
        li.className = "box-item";

        const num = document.createElement("span");
        num.className = "box-number";
        num.textContent = q != null ? String(q) : "";

        const text = document.createElement("span");
        text.className = "box-text";
        text.textContent = n ?? "";

        li.append(num, text);
        return li;
      }),
    );
    this.dispatchEvent(
      new CustomEvent("variant-box-list:filled", { bubbles: true }),
    );
  }
}
customElements.define("variant-box-list", VariantBoxList);

/* ------------------------------------------------------------------ */
/* <variant-spec-popup>                                                */
/*   Map value: [{ l: label, v: value, g: group, o: order }, ...].     */
/*   Children: [data-groups] target + JSON map.                        */
/*   Groups by `g`, sorts each group by `o`, builds nested accordion.  */
/* ------------------------------------------------------------------ */
class VariantSpecPopup extends VariantReactive {
  /**
   * @param {Array<{ l: string; v: string; g: string; o?: number }> | null} items
   */
  render(items) {
    const container = this.querySelector("[data-groups]");
    if (!container) return;

    if (!items || items.length === 0) {
      container.replaceChildren();
      return;
    }

    /** @type {Record<string, Array<{ l: string; v: string; g: string; o?: number }>>} */
    const grouped = {};
    /** @type {string[]} — first-seen order (fallback) */
    const seenOrder = [];
    for (const item of items) {
      if (!(item.g in grouped)) {
        grouped[item.g] = [];
        seenOrder.push(item.g);
      }
      const group = grouped[item.g];
      if (group) {
        group.push(item);
      }
    }
    for (const g of seenOrder) {
      if (grouped[g]) {
        grouped[g].sort((a, b) => (a.o ?? 0) - (b.o ?? 0));
      }
    }

    // Read optional theme-defined group order from sibling <script data-group-order>
    // Shopify richtext puts all items in one <p> separated by <br/> tags.
    // Replace <br> with a delimiter, then split on it.
    /** @type {string[]} */
    let themeOrder = [];
    try {
      const orderEl = this.querySelector("[data-group-order]");
      if (orderEl) {
        const tmp = document.createElement("div");
        tmp.innerHTML = (orderEl.textContent || orderEl.innerHTML || "")
          .replace(/<br\s*\/?>/gi, "\n");
        // Now split on newlines across all text content
        const raw = tmp.textContent || "";
        themeOrder = raw.split("\n")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
      }
    } catch (_) {
      // silently ignore — fall back to seenOrder
    }

    // Build final order: theme-specified groups first (case-insensitive + entity-decoded match),
    // then any remaining groups that weren't listed.
    /** @type {string[]} */
    const groupOrder = [];
    if (themeOrder.length > 0) {
      // Map decoded-lowercase theme names → actual group key
      const decodeEntities = (str) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = str;
        return txt.value;
      };
      const lowerToKey = Object.fromEntries(
        seenOrder.map((k) => [decodeEntities(k).toLowerCase(), k])
      );
      for (const name of themeOrder) {
        const key = lowerToKey[name];
        if (key && !groupOrder.includes(key)) {
          groupOrder.push(key);
        }
      }
      // Append any groups not mentioned in theme settings
      for (const k of seenOrder) {
        if (!groupOrder.includes(k)) {
          groupOrder.push(k);
        }
      }
    } else {
      groupOrder.push(...seenOrder);
    }

    container.replaceChildren(
      ...groupOrder.map((groupName, i) =>
        VariantSpecPopup.#buildGroup(
          groupName,
          grouped[groupName] || [],
          i === 0,
        ),
      ),
    );
  }

  /**
   * @param {string} s
   */
  static #titleCase(s) {
    return String(s)
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  /**
   * @param {string} groupName
   * @param {Array<{ l: string; v: string; g: string; o?: number }>} items
   * @param {boolean} isFirst
   */
  static #buildGroup(groupName, items, isFirst) {
    const wrap = document.createElement("div");
    wrap.className = "accordion-item" + (isFirst ? " active" : "");

    const header = document.createElement("button");
    header.type = "button";
    header.className = "accordion-header";
    header.textContent = VariantSpecPopup.#titleCase(groupName);

    const content = document.createElement("div");
    content.className = "accordion-content";

    const list = document.createElement("div");
    list.className = "spec-content-list";

    items.forEach((it) => {
      const row = document.createElement("div");
      row.className = "spec-item-row";
      const label = document.createElement("span");
      label.textContent = it.l ?? "";
      const value = document.createElement("span");
      value.textContent = it.v ?? "";
      row.append(label, value);
      list.append(row);
    });

    content.append(list);
    wrap.append(header, content);
    return wrap;
  }
}
customElements.define("variant-spec-popup", VariantSpecPopup);

/* ------------------------------------------------------------------ */
/* <variant-spec-list>                                                 */
/*   Map value: [{ l: label, v: value }, ...] — server is responsible  */
/*   for filtering/sorting. Renders into [data-list] as `.spec-item-row` */
/*   divs (matching the existing `.dimensions-list .dimension-item`    */
/*   visual pattern). Hides itself when empty.                         */
/* ------------------------------------------------------------------ */
class VariantSpecList extends VariantReactive {
  /**
   * @param {Array<{ l: string; v: string; o?: number }> | { items: Array<{ l: string; v: string; o?: number }>; image?: string | null } | null} data
   */
  render(data) {
    const list = this.querySelector("[data-list]");
    const imageEl = /** @type {HTMLImageElement | null} */ (
      this.querySelector("[data-image]")
    );
    const imageWrap = imageEl?.closest(".dimensions-image");

    // Data may be a bare items array or { items, image } object.
    const items = Array.isArray(data) ? data : (data?.items ?? []);
    const imageUrl = Array.isArray(data) ? null : (data?.image ?? null);

    if (imageEl) {
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageWrap?.classList.remove("is-hidden");
      } else {
        imageEl.removeAttribute("src");
        imageWrap?.classList.add("is-hidden");
      }
    }

    if (list) {
      const sorted = [...items].sort((a, b) => (a.o ?? 0) - (b.o ?? 0));
      list.replaceChildren(
        ...sorted.map(({ l, v }) => {
          const row = document.createElement("div");
          row.className = "dimension-item";
          const label = document.createElement("span");
          label.textContent = l ?? "";
          const value = document.createElement("span");
          value.textContent = v ?? "";
          row.append(label, value);
          return row;
        }),
      );
    }

    if (items.length === 0 && !imageUrl) {
      this.classList.add("is-hidden");
    } else {
      this.classList.remove("is-hidden");
    }
  }
}
customElements.define("variant-spec-list", VariantSpecList);

/* ------------------------------------------------------------------ */
/* <variant-featured-specs>                                            */
/*   Map value: [{ l: label, v: value, i: icon url, o: order }, ...]   */
/*   Server filters to featured=yes + has-icon and sorts client-side   */
/*   by `o`. Renders `.specs-list .spec-item` rows (icon + label +     */
/*   value). Hides itself when empty.                                  */
/* ------------------------------------------------------------------ */
class VariantFeaturedSpecs extends VariantReactive {
  /**
   * @param {Array<{ l: string; v: string; i?: string; o?: number }> | null} items
   */
  render(items) {
    const list = this.querySelector("[data-list]");
    if (!list) return;

    if (!items || items.length === 0) {
      this.classList.add("is-hidden");
      list.replaceChildren();
      return;
    }

    const sorted = [...items].sort((a, b) => (a.o ?? 0) - (b.o ?? 0));

    this.classList.remove("is-hidden");
    list.replaceChildren(
      ...sorted.map(({ l, v, i }) => {
        const item = document.createElement("div");
        item.className = "spec-item";

        const imgLabel = document.createElement("div");
        imgLabel.className = "spec-img-label";

        if (i) {
          const imgWrap = document.createElement("div");
          imgWrap.className = "img";
          const img = document.createElement("img");
          img.src = i;
          img.alt = "";
          img.loading = "lazy";
          imgWrap.append(img);
          imgLabel.append(imgWrap);
        }

        const label = document.createElement("span");
        label.textContent = l ?? "";
        imgLabel.append(label);

        const valueWrap = document.createElement("div");
        valueWrap.className = "value-spec";
        const value = document.createElement("span");
        value.textContent = v ?? "";
        valueWrap.append(value);

        item.append(imgLabel, valueWrap);
        return item;
      }),
    );
  }
}
customElements.define("variant-featured-specs", VariantFeaturedSpecs);

/* ------------------------------------------------------------------ */
/* <variant-bullet-points>                                             */
/*   Map value: [string, string, ...] — array of bullet point texts.  */
/*   Children: [data-bullet-list] target + JSON map.                  */
/*   Hides itself when empty.                                         */
/* ------------------------------------------------------------------ */
class VariantBulletPoints extends VariantReactive {
  /**
   * @param {Array<string> | null} bullets
   */
  render(bullets) {
    const list = this.querySelector("[data-bullet-list]");
    if (!list) return;

    if (!bullets || bullets.length === 0) {
      this.classList.add("is-hidden");
      list.replaceChildren();
      return;
    }

    this.classList.remove("is-hidden");
    list.replaceChildren(
      ...bullets.map((text) => {
        const li = document.createElement("li");
        li.className = "bullet-point-item";
        li.textContent = text ?? "";
        return li;
      }),
    );
  }
}
customElements.define("variant-bullet-points", VariantBulletPoints);

export { VariantReactive };
